/**
 * @fileoverview Cognito adapter for AuthPort.
 *
 * The only file in the app that imports `amazon-cognito-identity-js`. Every
 * Cognito concept — user pools, authentication details, challenge callbacks —
 * is confined here and translated into the port's vocabulary at the boundary.
 *
 * The pool is constructed from injected config rather than read from the
 * environment at module scope. That is the specific change that prevents the
 * previous failure mode, where a stale hardcoded pool id shipped in a green
 * build and only surfaced as a sign-in error against a pool that no longer
 * existed.
 *
 * Tokens are persisted to localStorage by the SDK under its own keys, which is
 * acceptable for dev; a secure-cookie strategy would be the XSS-resistant
 * choice for production.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  type IAuthenticationCallback,
} from "amazon-cognito-identity-js";
import type { AppConfig } from "../domain/config.js";
import type {
  AuthPort,
  NewPasswordChallenge,
  Session,
  SignInResult,
} from "../ports/auth.js";

function toSession(cs: CognitoUserSession, email: string): Session {
  const id = cs.getIdToken();
  const payload = id.decodePayload() as Record<string, unknown>;
  return {
    idToken: id.getJwtToken(),
    accessToken: cs.getAccessToken().getJwtToken(),
    refreshToken: cs.getRefreshToken().getToken(),
    email: (payload.email as string) ?? email,
    sub: (payload.sub as string) ?? "",
    agencyId: (payload["custom:agency_id"] as string) ?? null,
    role: (payload["custom:role"] as string) ?? null,
    name: (payload.name as string) ?? null,
    givenName: (payload.given_name as string) ?? null,
    familyName: (payload.family_name as string) ?? null,
    nickname: (payload.nickname as string) ?? null,
    expiresAt: new Date((id.getExpiration() ?? 0) * 1000),
  };
}

export class CognitoAuthAdapter implements AuthPort {
  private readonly pool: CognitoUserPool;

  constructor(config: AppConfig) {
    this.pool = new CognitoUserPool({
      UserPoolId: config.cognitoUserPoolId,
      ClientId: config.cognitoClientId,
    });
  }

  async signIn(email: string, password: string): Promise<SignInResult> {
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    const auth = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise<SignInResult>((resolve, reject) => {
      const callbacks: IAuthenticationCallback = {
        onSuccess(result) {
          resolve({ kind: "SESSION", session: toSession(result, email) });
        },
        onFailure(err) {
          reject(err);
        },
        newPasswordRequired(userAttributes, requiredAttributes) {
          // Strip read-only attrs Cognito refuses on completeNewPasswordChallenge.
          // (email_verified is the typical offender on Admin-created users.)
          const filtered = { ...userAttributes } as Record<string, unknown>;
          delete filtered.email_verified;
          delete filtered.email; // re-supplied explicitly on completion
          resolve({
            kind: "NEW_PASSWORD_REQUIRED",
            holder: user,
            email,
            userAttributes: filtered,
            requiredAttributes: requiredAttributes ?? [],
          });
        },
      };
      user.authenticateUser(auth, callbacks);
    });
  }

  async completeNewPassword(
    challenge: NewPasswordChallenge,
    newPassword: string,
    email: string,
  ): Promise<Session> {
    // The port keeps `holder` opaque; this adapter is the one place that knows
    // it is the CognitoUser that raised the challenge.
    const user = challenge.holder as CognitoUser;

    // Send ONLY the attributes Cognito actually asked for. Unconditionally
    // passing { email } fails with
    //   NotAuthorizedException: Cannot modify an already provided email
    // for any admin-created user, because admin-create-user already set it —
    // which is every user in this pool, since public sign-up is disabled. The
    // challenge's requiredAttributes is the authoritative list; for a pool with
    // no required user-supplied attributes it is empty, and an empty map is the
    // correct payload.
    const attributes: Record<string, string> = {};
    for (const raw of challenge.requiredAttributes) {
      // Cognito prefixes these as `userAttributes.<name>`.
      const name = raw.replace(/^userAttributes\./, "");
      if (name === "email") attributes.email = email;
    }

    return new Promise<Session>((resolve, reject) => {
      user.completeNewPasswordChallenge(
        newPassword,
        attributes,
        {
          onSuccess(result) {
            resolve(toSession(result, email));
          },
          onFailure(err) {
            reject(err);
          },
        },
      );
    });
  }

  async requestPasswordReset(email: string): Promise<void> {
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    return new Promise<void>((resolve, reject) => {
      user.forgotPassword({
        onSuccess() {
          resolve();
        },
        onFailure(err) {
          reject(err);
        },
        inputVerificationCode() {
          // Cognito calls this once the code has been dispatched. For our UX
          // that IS success — we always advance to the reset step on resolve.
          resolve();
        },
      });
    });
  }

  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    return new Promise<void>((resolve, reject) => {
      user.confirmPassword(code, newPassword, {
        onSuccess() {
          resolve();
        },
        onFailure(err) {
          reject(err);
        },
      });
    });
  }

  async signOut(): Promise<void> {
    this.pool.getCurrentUser()?.signOut();
  }

  async currentSession(): Promise<Session | null> {
    const user = this.pool.getCurrentUser();
    if (!user) return null;

    return new Promise<Session | null>((resolve) => {
      user.getSession((err: Error | null, cs: CognitoUserSession | null) => {
        if (err || !cs || !cs.isValid()) {
          resolve(null);
          return;
        }
        const payload = cs.getIdToken().decodePayload() as Record<string, unknown>;
        const email = (payload.email as string) ?? user.getUsername();
        resolve(toSession(cs, email));
      });
    });
  }
}
